import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { gsap } from 'gsap';
import { createServiceNodes } from '../utils/serviceNodes';
import { setupLights } from '../utils/lighting';
import { setupEnvironment } from '../utils/environment';
import { setupInteractions } from '../utils/interactions';
import { services } from '../utils/serviceData';

export const useThreeScene = ({ canvas, onServiceClick, onButtonClick, onHelpClick, isModalOpen, cinematicMode }) => {
  const sceneRef = useRef();
  const cameraRef = useRef();
  const rendererRef = useRef();
  const controlsRef = useRef();
  const composerRef = useRef();
  const serviceNodesRef = useRef([]);
  const animationIdRef = useRef();
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize Three.js scene
  useEffect(() => {
    if (!canvas) return;

    // Scene setup
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75, 
      window.innerWidth / window.innerHeight, 
      0.1, 
      1000
    );
    const renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
    });

    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;

    camera.position.set(0, 2, -4);

    // Setup controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.screenSpacePanning = false;
    controls.maxPolarAngle = Math.PI / 1.5;
    controls.minPolarAngle = Math.PI / 3;
    controls.enableZoom = false;
    controls.rotateSpeed = 0.5;
    controls.target.set(0, 1.5, 0);

    // Setup post-processing
    const composer = new EffectComposer(renderer);
    const renderPass = new RenderPass(scene, camera);
    composer.addPass(renderPass);
    
    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      0.5,   // strength
      0.4,   // radius
      0.85   // threshold
    );
    composer.addPass(bloomPass);

    // Store references
    sceneRef.current = scene;
    cameraRef.current = camera;
    rendererRef.current = renderer;
    controlsRef.current = controls;
    composerRef.current = composer;

    // Setup scene elements
    setupLights(scene);
    setupEnvironment(scene, cinematicMode);

    // Setup interactions after service nodes are created
    let interactionCleanup;
    createServiceNodes(scene, services, onButtonClick, onHelpClick, camera, cinematicMode).then(nodes => {
      serviceNodesRef.current = nodes;
      const interactions = setupInteractions({
        scene,
        camera,
        canvas,
        serviceNodes: nodes,
        onServiceClick,
        isModalOpen
      });
      interactionCleanup = interactions.cleanup;
      // Start movement animation
      interactions.animateMovement(controls);
    });

    // Handle window resize
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
      composer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleResize);

    // Animation loop
    const animate = () => {
      animationIdRef.current = requestAnimationFrame(animate);
      controls.update();
      composer.render();
    };

    animate();
    setIsInitialized(true);

    // Start entrance animations
    setTimeout(() => {
      gsap.fromTo(camera.position, 
        { x: 0, y: 10, z: 20 },
        { 
          x: 0, 
          y: 1.5, 
          z: 6, 
          duration: 3,
          ease: "power2.inOut"
        }
      );
    }, 1000);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
      if (interactionCleanup) {
        interactionCleanup();
      }
      // Cleanup button detection
      serviceNodesRef.current.forEach(node => {
        if (node.userData.cleanup) {
          node.userData.cleanup();
        }
      });
      composer.dispose();
      renderer.dispose();
    };
  }, [canvas, onServiceClick, onButtonClick, onHelpClick, isModalOpen]);

  // Handle cinematic mode changes
  useEffect(() => {
    if (!sceneRef.current) return;
    
    // Update environment background
    setupEnvironment(sceneRef.current, cinematicMode);
    
    // Update remote visibility
    if (serviceNodesRef.current.length > 0) {
      const remoteMesh = sceneRef.current.getObjectByName('remoteMesh');
      if (remoteMesh) {
        remoteMesh.visible = !cinematicMode;
      }
    }
  }, [cinematicMode]);

  // Navigation function
  const navigateToSection = (section) => {
    if (!isInitialized || !cameraRef.current || !controlsRef.current) return;

    let targetPosition;
    let lookAtPosition = new THREE.Vector3(0, 1.5, 0);
    
    // Find the service node
    if (section !== 'home' && section !== 'about' && section !== 'contact') {
      const serviceNode = serviceNodesRef.current.find(node => node.userData.id === section);
      
      if (serviceNode) {
        // Position in front of the service node
        const direction = new THREE.Vector3().subVectors(serviceNode.position, lookAtPosition).normalize();
        targetPosition = new THREE.Vector3().copy(serviceNode.position).sub(direction.multiplyScalar(3));
        targetPosition.y = 1.5;
        lookAtPosition.copy(serviceNode.position);
      }
    } else if (section === 'home') {
      targetPosition = new THREE.Vector3(0, 1.5, 6);
    } else if (section === 'about') {
      targetPosition = new THREE.Vector3(-6, 1.5, 0);
    } else if (section === 'contact') {
      targetPosition = new THREE.Vector3(6, 1.5, 0);
    }
    
    if (targetPosition) {
      // Animate camera movement
      gsap.to(cameraRef.current.position, {
        x: targetPosition.x,
        y: targetPosition.y,
        z: targetPosition.z,
        duration: 2,
        ease: "power2.inOut",
        onUpdate: () => {
          controlsRef.current.target.lerp(lookAtPosition, 0.1);
        }
      });
    }
  };

  return {
    scene: sceneRef.current,
    camera: cameraRef.current,
    renderer: rendererRef.current,
    controls: controlsRef.current,
    serviceNodes: serviceNodesRef.current,
    navigateToSection
  };
};